import { action, runInAction, makeObservable } from "mobx";
import EscaninhoPeticaoApi from "../api/EscaninhoPeticaoApi";
import InformacoesProcessoApi from "../api/InformacoesProcessoApi";
import { Processo } from "../modelo";
import { PeticaoPendente } from "../modelo/PeticaoApreciada";
import { PecaDto } from "../modelo/dto/PecaDto";
import FiltroArvorePeticaoStore from "../store/FiltroArvorePeticaoStore";
import FiltroStore from "../store/FiltroStore";
import ProcessoStore from "../store/ProcessoStore";

class EscaninhoPeticaoService {
    constructor() {
        makeObservable(this, {
            toggleDrawerFiltro: action,
            limparFiltroPeticoes: action,
            filtrarPorSitPeticao: action,
            filtrarPorEscaninhoPeticao: action,
            filtrarPorPeticaoETipoMinuta: action
        });
    }

    private ultimoCarregamentoUnitarioPeticoesPendentes = new Map<number, number>();

    toggleDrawerFiltro() {
        FiltroStore.drawerIsOpen = false;
        FiltroArvorePeticaoStore.drawerAberto = !FiltroArvorePeticaoStore.drawerAberto;
    }

    limparFiltroPeticoes = () => {
        FiltroStore.situacaoPeticaoFilter = ''
        FiltroStore.incluirForaDoGabineteFilter = true;
        FiltroStore.naoPossuiMinuta = false;
        FiltroStore.possuiMinutaDecisao = false;
        FiltroStore.possuiMinutaDespacho = false;
        FiltroStore.possuiMinutaVoto = false;
        FiltroStore.possuiMinutaVotoAgravoProvido = false;
        FiltroStore.situacaoMinutaFilter = [];
        FiltroArvorePeticaoStore.situacaoPeticao = '';
        FiltroArvorePeticaoStore.situacaoMinutaPeticao = '';
    };

    filtrarPorSitPeticao = (situacaoPeticao: string) => {
        this.limparFiltroPeticoes();
        FiltroStore.situacaoPeticaoFilter = situacaoPeticao;
        FiltroArvorePeticaoStore.situacaoPeticao = situacaoPeticao;
        FiltroStore.procurar(false);
    };

    filtrarPorEscaninhoPeticao = () => {
        this.limparFiltroPeticoes();
        FiltroStore.procurar(false);
    };

    filtrarPorPeticaoETipoMinuta = (situacaoPeticao: string, situacaoMinuta: string) => {
        this.limparFiltroPeticoes();
        FiltroArvorePeticaoStore.situacaoPeticao = situacaoPeticao;
        FiltroArvorePeticaoStore.situacaoMinutaPeticao = situacaoMinuta;
        FiltroStore.situacaoPeticaoFilter = situacaoPeticao;
        if (situacaoMinuta === 'Inexistente') {
            FiltroStore.naoPossuiMinuta = true;
        } else {
            FiltroStore.possuiMinutaDecisao = true;
            FiltroStore.possuiMinutaDespacho = true;
            FiltroStore.situacaoMinutaFilter = [situacaoMinuta];
        }
        FiltroStore.procurar(false);
    };

    async carregarPeticoesPendentes(gabinete: string, controlerCarregamentoProcessos: AbortController) {
        const inicioCarregamentoGlobal = Date.now();
        runInAction(() => {
            ProcessoStore.carregandoPeticoesPendentes = true;
            ProcessoStore.carregouPeticoesPendentes = false;
            ProcessoStore.carregandoPeticoesPendentesEscaninho = true;
        });

        try {
            const idsProcessos = ProcessoStore._processos.map(proc => proc.identificadorProcesso);

            const processosPeticoes = await EscaninhoPeticaoApi.buscarPeticoesPendentes(gabinete, idsProcessos, controlerCarregamentoProcessos.signal);
            if (processosPeticoes && Array.isArray(processosPeticoes)) {
                runInAction(() => {
                    const processosPorId = new Map(ProcessoStore._processos.map(processo => [processo.identificadorProcesso, processo]));

                    ProcessoStore._processos.forEach(processo => {
                        if (!this.unitarioCarregadoAposInicioDoGlobal(processo.identificadorProcesso, inicioCarregamentoGlobal)) {
                            processo.peticoesPendentes = [];
                        }
                    });

                    const processosForaDoGE: Processo[] = [];
                    processosPeticoes.forEach(procPeticao => {
                        const processo = processosPorId.get(procPeticao.identificadorProcesso);
                        if (processo) {
                            if (!this.unitarioCarregadoAposInicioDoGlobal(processo.identificadorProcesso, inicioCarregamentoGlobal)) {
                                processo.peticoesPendentes = procPeticao?.peticoesPendentes || [];
                            }
                        } else {
                            processosForaDoGE.push(Processo.novo(procPeticao));
                        }
                    });

                    ProcessoStore._processosForaDoGE = processosForaDoGE;
                });
            }
            runInAction(() => {
                ProcessoStore.carregouPeticoesPendentes = true;
            });
        } catch (erro) {
            console.error('Erro ao carregar petições pendentes:', erro);
        } finally {
            runInAction(() => {
                ProcessoStore.carregandoPeticoesPendentes = false;
                ProcessoStore.carregandoPeticoesPendentesEscaninho = false;
            });
        }
    }

    async carregarPeticoesPendentesPorProcesso(idProcesso: number) {
        runInAction(() => {
            ProcessoStore.carregandoPeticoesPendentes = true;
        });
        try {
            const peticoes = await InformacoesProcessoApi.consultarPeticoesPendentes(idProcesso);
            const processo = ProcessoStore._processos.find(proc => proc.identificadorProcesso === idProcesso);
            runInAction(() => {
                if (processo) {
                    processo.peticoesPendentes = peticoes.map(this.mapearPeticaoPendente);
                    this.ultimoCarregamentoUnitarioPeticoesPendentes.set(idProcesso, Date.now());
                }
            });
        } finally {
            runInAction(() => {
                ProcessoStore.carregandoPeticoesPendentes = false;
            });
        }
    }

    private unitarioCarregadoAposInicioDoGlobal(idProcesso: number, inicioCarregamentoGlobal: number): boolean {
        const ultimoCarregamentoUnitario = this.ultimoCarregamentoUnitarioPeticoesPendentes.get(idProcesso);
        return ultimoCarregamentoUnitario > inicioCarregamentoGlobal;
    }

    private mapearPeticaoPendente = (peticao: PecaDto): PeticaoPendente => {
        const peticaoPendente = new PeticaoPendente();
        peticaoPendente.idUnico = peticao.idUnicoDocumento;
        peticaoPendente.juntadoEm = peticao.juntadoEm;
        peticaoPendente.criadoEm = peticao.criadoEm ? new Date(peticao.criadoEm) : undefined;
        peticaoPendente.numeroPeticao = peticao.numPeticao || peticao.id;
        peticaoPendente.numeroProcesso = peticao.idProcesso;
        peticaoPendente.situacaoPeticao = peticao.descricaoPeticaoStatus || (peticao.apreciado ? 'Apreciada' : 'Não apreciada');
        peticaoPendente.titulo = peticao.titulo || peticao.assuntoPeticao || peticao.tipo?.nomeTipoProcessoDocumento;
        peticaoPendente.tipo = peticao.tipo?.nomeTipoProcessoDocumento;
        return peticaoPendente;
    }
}

export default new EscaninhoPeticaoService();
